// The role a dropped song file can take in the files table (ART-218): the one Master, any variant KIND, or a
// Custom label. Built on the variant-kind vocabulary; Master is a separate, singular role (a song has exactly
// one). The table auto-suggests a role from the filename — the legacy exports encode it — and the user overrides.

import { VARIANT_KINDS, CUSTOM_VARIANT_KEY, labelForVariantKey } from "./releaseVariantKinds.js";

/** The singular master role — distinct from the variant kinds. */
export const MASTER_ROLE = "master";

/** A row whose role hasn't been decided — an EMPTY dropdown. The filename matched no keyword and no master
 * slot was free, so we make no guess: the file is neither uploaded nor committed until the user picks a role,
 * and a row still on this value is scrapped when the modal closes. */
export const NO_ROLE = "";

export { CUSTOM_VARIANT_KEY };

/** Role dropdown options: Master, then the variant kinds, then Custom. */
export const SONG_FILE_ROLE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: MASTER_ROLE, label: "Master" },
  ...VARIANT_KINDS.map((k) => ({ value: k.key, label: k.label })),
  { value: CUSTOM_VARIANT_KEY, label: "Custom…" },
];

/** The stored label a role commits with. Master → "Master"; a kind → its label; Custom → the caller's text. */
export function labelForRole(role: string, customLabel?: string): string {
  if (role === MASTER_ROLE) return "Master";
  if (role === CUSTOM_VARIANT_KEY) return (customLabel ?? "").trim() || "Custom";
  return labelForVariantKey(role);
}

/** Match a stored variant label back to its role key (for pre-loading existing variants into the table). */
export function roleForVariantLabel(label: string): string {
  const hit = VARIANT_KINDS.find((k) => k.label.toLowerCase() === label.trim().toLowerCase());
  return hit ? hit.key : CUSTOM_VARIANT_KEY;
}

/**
 * Best-guess role from a filename, overridable. Specific keywords before generic (a name can contain both
 * "atmos" and "ir"); anything with no variant keyword defaults to Master — the master is the file that isn't
 * one of the alternates.
 */
export function suggestRoleFromFilename(filename: string): string {
  const s = filename.toLowerCase();
  if (s.includes("spatial")) return "spatial";
  if (s.includes("binaural")) return "atmos_binaural";
  if (s.includes("adm")) return "atmos_adm";
  if (s.includes("atmosir") || s.endsWith(".atmosir") || (s.includes("atmos") && /(^|[^a-z])ir([^a-z]|$)/.test(s))) return "atmos_ir";
  if (s.includes("instrumental")) return "instrumental";
  if (s.includes("acapella") || s.includes("accapella") || s.includes("a capella")) return "acapella";
  if (s.includes("stem")) return "stems";
  return MASTER_ROLE;
}
